import {copyAnalysisSteps} from 'analysis-tool/AnalysisActions';
import {ARCHIVED_ANALYSIS_TOOLTIP_TEXT} from 'analysis-tool/AnalysisToolConstants';
import {AnalysisGraphQL, AnalysisViewGraphQL, isAnalysisOutputStepType} from 'analysis-tool/AnalysisTypes';
import {OverflowDataViewAction} from 'analysis-tool/data-view/DataViewContext';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {recordAnalysisAction} from 'analysis-tool/ui/debugAnalysisActionHistory';
import isArchived from 'archive/isArchived';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {withLoadingScreen} from 'util/Promises';

const CONFLICTING_VIEWS_MESSAGE = 'This view cannot be copied because it shares steps with other views.';

/**
 * Check if two views share nodes, and copying one view would unexpectedly copy the other.
 */
function doViewsConflict(view1: AnalysisViewGraphQL, view2: AnalysisViewGraphQL): boolean {
  const startNode1 = view1.lineage[0];
  const startNode2 = view2.lineage[0];
  const endNode1 = view1.lineage[view1.lineage.length - 1];
  const endNode2 = view2.lineage[view2.lineage.length - 1];

  // If the views do not start with the same node, they do not conflict
  if (startNode1.step.id !== startNode2.step.id) {
    return false;
  }

  // Views that end in the same REGRESSION step are not considered conflicting, since the three views created
  // by a REGRESSION are presented to the user as a single unit, and one would expect them to be copied
  // as a unit.
  if (endNode1.step.stepType === PipelineStepType.REGRESSION && endNode1.step.id === endNode2.step.id) {
    return false;
  }

  return true;
}

export default function getCopyViewAction(
  analysis: AnalysisGraphQL,
  view: AnalysisViewGraphQL
): OverflowDataViewAction | null {
  if (isAnalysisReadOnly(analysis)) {
    return null;
  }

  if (
    view.lineage.some(
      (node) =>
        isAnalysisOutputStepType(node.step.stepType) ||
        node.step.statusCode === PipelineStepStatusCode.RUNNING ||
        node.step.statusCode === PipelineStepStatusCode.BLOCKED
    )
  ) {
    return null;
  }

  // More than one view can share the same steps. In that scenario, copying a view's step will create more
  // than one new view. This may be unexpected to the user, unless – as is the case of the three views
  // created by a `REGRESSION` step – the user understands the views to be be a single unit.
  const hasConflictingView = // check for >1 conflicts, because a view will always conflict with itself
    analysis.views.filter((otherView) => doViewsConflict(view, otherView)).length > 1;

  return {
    icon: 'copy',
    onClick: () => {
      recordAnalysisAction('COPY_VIEW', {
        analysisId: analysis.id,
        stepIds: view.lineage.map((node) => node.step.id),
        firstStepType: view.lineage[0].step.stepType,
      });
      withLoadingScreen(
        copyAnalysisSteps(
          analysis.id,
          view.lineage.map((node) => node.step.id)
        )
      );
    },
    label: 'Copy view',
    isDisabled: isArchived(analysis) || hasConflictingView,
    helpText: isArchived(analysis)
      ? ARCHIVED_ANALYSIS_TOOLTIP_TEXT
      : hasConflictingView
        ? CONFLICTING_VIEWS_MESSAGE
        : undefined,
  };
}
